// ── BCHNostr — Paid business verification ─────────────────────────
//
// Adds a PAID, TIME-LIMITED verification layer on top of the EXISTING
// business identity (kind 30499, see lib/business.ts). It does NOT create a
// business, a keypair, a wallet, or a second merchant database. It reuses:
//
//   · the existing business Nostr identity (the 30499 author pubkey),
//   · the existing BCH payment rails (useTipWallet → PLATFORM_FEE_ADDRESS),
//   · the existing on-chain audit (lib/paymentAudit.ts),
//   · the existing operator-grant trust model (lib/nip05verify.ts pattern),
//   · the existing kind 30499 profile as the merchant-discovery signal that
//     BCH Local consumes — we simply stamp a verification marker onto it.
//
// ── Trust model (mirrors paid NIP-05) ─────────────────────────────
//
// A business publishes a CLAIM (a self-signed receipt carrying the payment
// txid). A claim proves nothing on its own — anyone can mint one. The badge
// is only conferred by a GRANT signed by a trusted issuer (the operator or
// the backend grant key, GRANT_ISSUERS). The grant carries the verification
// window (start + expiry) and can be revoked. Consumers must therefore only
// ever trust grants author-filtered to GRANT_ISSUERS.
//
// What verification MEANS: "this business completed BCHNostr's business
// verification process." It is NOT a claim that the business is currently
// accepting BCH, nor a government/legal verification, nor a BCHPay
// requirement. Never render "Currently accepting BCH" from this signal.

import type { Event } from 'nostr-tools';
import { GRANT_ISSUERS, isGrantIssuer, PLATFORM_FEE_ADDRESS } from './platform';

// ── Kinds, tags, d-tags ───────────────────────────────────────────

/** All business-verification side-data rides on NIP-78 app data. */
export const BUSINESS_VERIFY_KIND = 30078;

/** Claim (published BY the business as a payment receipt). One per business. */
export const BUSINESS_VERIFY_CLAIM_D = 'bchnostr/business-verify';
export const BUSINESS_VERIFY_CLAIM_TAG = 'bch-business-verify-claim';

/** Grant (published by a trusted issuer — the actual trust boundary). */
export const BUSINESS_VERIFY_GRANT_D_PREFIX = 'bchnostr/business-verify-grant/';
export const BUSINESS_VERIFY_GRANT_TAG = 'bch-business-verify';

/** Operator-configurable fee. Honoured only from the operator key. */
export const BUSINESS_VERIFY_CONFIG_D = 'bchnostr/business-verify-config';

/**
 * Marker stamped onto the EXISTING kind 30499 profile so the existing
 * merchant-discovery query (which BCH Local consumes) can tell a verified
 * business apart without a second lookup. A relay-indexable `t` tag plus a
 * `verified` tag carrying the expiry. This is a self-asserted CONVENIENCE
 * hint only — the authoritative signal is always the issuer-signed grant,
 * so readers still confirm against `parseVerificationGrant`.
 */
export const BUSINESS_VERIFIED_MARKER_TAG = 'bch-verified-business';

// ── Economics ─────────────────────────────────────────────────────

/** Default verification fee in USD. Admin-configurable (never hard-coded BCH). */
export const DEFAULT_BUSINESS_VERIFY_FEE_USD = 10;

/** Verification lasts one year. */
export const BUSINESS_VERIFY_PERIOD_DAYS = 365;
export const BUSINESS_VERIFY_PERIOD_SECS = BUSINESS_VERIFY_PERIOD_DAYS * 86400;

/** Owner-facing expiry reminders, in days before expiration. */
export const EXPIRY_NOTICE_DAYS = [30, 7, 1] as const;

// ── Status ────────────────────────────────────────────────────────

export type BusinessVerifyStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'EXPIRED';

const TXID_RE = /^[0-9a-f]{64}$/;
const HEX64 = /^[0-9a-f]{64}$/;

// ── Fee config (admin-settable) ───────────────────────────────────

export interface VerifyFeeConfig {
  feeUsd: number;
}

/** Build the operator-only fee-config event. */
export function buildVerifyFeeConfigEvent(feeUsd: number): {
  kind: number; tags: string[][]; content: string;
} {
  const safe = Number.isFinite(feeUsd) && feeUsd > 0 ? Math.round(feeUsd * 100) / 100 : DEFAULT_BUSINESS_VERIFY_FEE_USD;
  return {
    kind: BUSINESS_VERIFY_KIND,
    tags: [
      ['d', BUSINESS_VERIFY_CONFIG_D],
      ['alt', 'BCHNostr business verification fee configuration'],
    ],
    content: JSON.stringify({ feeUsd: safe }),
  };
}

/**
 * Parse a fee-config event. Honoured ONLY from the operator/issuer set, so
 * a random user cannot lower the fee. Returns null if invalid/untrusted.
 */
export function parseVerifyFeeConfig(ev: Event | null | undefined): VerifyFeeConfig | null {
  if (!ev || ev.kind !== BUSINESS_VERIFY_KIND) return null;
  if (!isGrantIssuer(ev.pubkey)) return null;
  const d = ev.tags.find(t => t[0] === 'd')?.[1];
  if (d !== BUSINESS_VERIFY_CONFIG_D) return null;
  try {
    const parsed = JSON.parse(ev.content) as { feeUsd?: unknown };
    const feeUsd = typeof parsed.feeUsd === 'number' && parsed.feeUsd > 0 ? parsed.feeUsd : NaN;
    if (!Number.isFinite(feeUsd)) return null;
    return { feeUsd };
  } catch {
    return null;
  }
}

/** Convert a USD fee to satoshis using the live BCH price. 0 when unknown. */
export function verifyFeeSats(feeUsd: number, bchPriceUsd: number | null): number {
  if (!bchPriceUsd || bchPriceUsd <= 0) return 0;
  return Math.round((feeUsd / bchPriceUsd) * 1e8);
}

// ── Claim (business-published payment receipt) ────────────────────

export interface VerifyClaim {
  /** The business pubkey that published the claim (= the business identity). */
  pubkey: string;
  txid: string;
  amountSats: number;
  claimedAt: number;
  event: Event;
}

/** Build the claim event the business publishes after paying. */
export function buildVerifyClaimEvent(txid: string, amountSats: number): {
  kind: number; tags: string[][]; content: string;
} {
  const now = Math.floor(Date.now() / 1000);
  const cleanTxid = TXID_RE.test((txid || '').toLowerCase()) ? txid.toLowerCase() : '';
  return {
    kind: BUSINESS_VERIFY_KIND,
    tags: [
      ['d', BUSINESS_VERIFY_CLAIM_D],
      ['t', BUSINESS_VERIFY_CLAIM_TAG],
      ['alt', 'BCHNostr business verification payment'],
      ...(cleanTxid ? [['fee', cleanTxid, String(amountSats), PLATFORM_FEE_ADDRESS]] : []),
    ],
    content: JSON.stringify({ txid: cleanTxid, amountSats, claimedAt: now }),
  };
}

/** Parse a claim event, or null. */
export function parseVerifyClaim(ev: Event | null | undefined): VerifyClaim | null {
  if (!ev || ev.kind !== BUSINESS_VERIFY_KIND) return null;
  const d = ev.tags.find(t => t[0] === 'd')?.[1];
  if (d !== BUSINESS_VERIFY_CLAIM_D) return null;
  if (!ev.tags.some(t => t[0] === 't' && t[1] === BUSINESS_VERIFY_CLAIM_TAG)) return null;
  let txid = '';
  let amountSats = 0;
  let claimedAt = ev.created_at;
  try {
    const c = JSON.parse(ev.content) as { txid?: string; amountSats?: number; claimedAt?: number };
    if (typeof c.txid === 'string') txid = c.txid.toLowerCase();
    if (typeof c.amountSats === 'number') amountSats = c.amountSats;
    if (typeof c.claimedAt === 'number') claimedAt = c.claimedAt;
  } catch { /* fall back to tag */ }
  // Fall back to the `fee` tag for the txid if content lacked it.
  if (!TXID_RE.test(txid)) {
    const feeTag = ev.tags.find(t => t[0] === 'fee');
    if (feeTag && TXID_RE.test((feeTag[1] || '').toLowerCase())) txid = feeTag[1].toLowerCase();
  }
  return { pubkey: ev.pubkey, txid, amountSats, claimedAt, event: ev };
}

// ── Grant (issuer-signed — the trust boundary) ────────────────────

export interface VerifyGrant {
  /** The business the grant verifies. */
  businessPubkey: string;
  /** Who issued the grant (must be a trusted issuer to be honoured). */
  issuedBy: string;
  /** Verification window. */
  startAt: number;
  expiresAt: number;
  /** Payment txid the grant was issued against (audit trail). */
  txid: string;
  /** True when the grant is a revocation (badge withdrawn). */
  revoked: boolean;
  issuedAt: number;
  event: Event;
}

export function verifyGrantDTag(businessPubkey: string): string {
  return `${BUSINESS_VERIFY_GRANT_D_PREFIX}${businessPubkey.toLowerCase()}`;
}

/**
 * Build a verification grant (issued by the operator/backend).
 *   startAt   — verification start (defaults to now),
 *   expiresAt — defaults to startAt + 1 year,
 *   revoked   — pass true to withdraw a badge without deleting anything.
 */
export function buildVerifyGrantEvent(opts: {
  businessPubkey: string;
  issuedBy: string;
  txid?: string;
  startAt?: number;
  expiresAt?: number;
  revoked?: boolean;
}): { kind: number; tags: string[][]; content: string } {
  const now = Math.floor(Date.now() / 1000);
  const startAt = opts.startAt ?? now;
  const expiresAt = opts.expiresAt ?? startAt + BUSINESS_VERIFY_PERIOD_SECS;
  const txid = TXID_RE.test((opts.txid || '').toLowerCase()) ? opts.txid!.toLowerCase() : '';
  const businessPubkey = opts.businessPubkey.toLowerCase();
  return {
    kind: BUSINESS_VERIFY_KIND,
    tags: [
      ['d', verifyGrantDTag(businessPubkey)],
      ['t', BUSINESS_VERIFY_GRANT_TAG],
      ['p', businessPubkey],
      ['alt', opts.revoked ? 'BCHNostr business verification revoked' : 'BCHNostr verified business'],
    ],
    content: JSON.stringify({
      businessPubkey,
      issuedBy: opts.issuedBy.toLowerCase(),
      startAt,
      expiresAt,
      txid,
      revoked: !!opts.revoked,
      issuedAt: now,
    }),
  };
}

/**
 * Parse a grant event. Returns null unless it was signed by a TRUSTED ISSUER
 * — a grant from anyone else is meaningless and must never confer a badge.
 * (Signature validity is guaranteed by the query/relay layer; this enforces
 * the author allow-list, the real trust boundary.)
 */
export function parseVerificationGrant(ev: Event | null | undefined): VerifyGrant | null {
  if (!ev || ev.kind !== BUSINESS_VERIFY_KIND) return null;
  if (!isGrantIssuer(ev.pubkey)) return null;
  const d = ev.tags.find(t => t[0] === 'd')?.[1] || '';
  if (!d.startsWith(BUSINESS_VERIFY_GRANT_D_PREFIX)) return null;

  let businessPubkey = ev.tags.find(t => t[0] === 'p')?.[1]?.toLowerCase() || '';
  let issuedBy = ev.pubkey.toLowerCase();
  let startAt = 0;
  let expiresAt = 0;
  let txid = '';
  let revoked = false;
  const issuedAt = ev.created_at;

  try {
    const c = JSON.parse(ev.content) as Record<string, unknown>;
    if (typeof c.businessPubkey === 'string') businessPubkey = c.businessPubkey.toLowerCase();
    if (typeof c.issuedBy === 'string') issuedBy = c.issuedBy.toLowerCase();
    if (typeof c.startAt === 'number') startAt = c.startAt;
    if (typeof c.expiresAt === 'number') expiresAt = c.expiresAt;
    if (typeof c.txid === 'string') txid = c.txid.toLowerCase();
    if (c.revoked === true) revoked = true;
  } catch {
    // Empty/malformed content is treated as a revocation (badge withdrawn).
    revoked = true;
  }

  if (!HEX64.test(businessPubkey)) return null;
  // The d-tag must match the business pubkey it claims to verify.
  if (d.slice(BUSINESS_VERIFY_GRANT_D_PREFIX.length) !== businessPubkey) return null;

  return { businessPubkey, issuedBy, startAt, expiresAt, txid, revoked, issuedAt, event: ev };
}

/** Collapse a set of grant events to the newest valid grant per business. */
export function newestGrantsByBusiness(events: Event[]): Map<string, VerifyGrant> {
  const byBiz = new Map<string, VerifyGrant>();
  for (const ev of events) {
    const g = parseVerificationGrant(ev);
    if (!g) continue;
    const existing = byBiz.get(g.businessPubkey);
    if (!existing || g.issuedAt > existing.issuedAt) byBiz.set(g.businessPubkey, g);
  }
  return byBiz;
}

// ── Status computation ────────────────────────────────────────────

export interface VerificationState {
  status: BusinessVerifyStatus;
  grant: VerifyGrant | null;
  claim: VerifyClaim | null;
  /** Verification window, when a (non-revoked) grant exists. */
  startAt: number;
  expiresAt: number;
  /** Whole days until expiry (negative once expired). 0 when not verified. */
  daysUntilExpiry: number;
}

/**
 * Derive the verification status from the newest grant and (optionally) a
 * claim. The GRANT is authoritative — the claim only lets us show PENDING
 * while the business has paid but the grant hasn't been issued yet.
 *
 *   VERIFIED   — a non-revoked grant whose window covers `now`.
 *   EXPIRED    — a non-revoked grant whose window has passed.
 *   PENDING    — a claim exists but no active grant (paid, awaiting confirmation).
 *   UNVERIFIED — nothing, or a revoked grant with no fresh claim.
 */
export function computeVerificationState(
  grant: VerifyGrant | null,
  claim: VerifyClaim | null,
  now: number = Math.floor(Date.now() / 1000),
): VerificationState {
  const base = { grant, claim, startAt: 0, expiresAt: 0, daysUntilExpiry: 0 };

  if (grant && !grant.revoked && grant.expiresAt > 0) {
    const daysUntilExpiry = Math.floor((grant.expiresAt - now) / 86400);
    if (now >= grant.startAt && now < grant.expiresAt) {
      return { ...base, status: 'VERIFIED', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
    }
    if (now >= grant.expiresAt) {
      return { ...base, status: 'EXPIRED', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
    }
    // Grant issued but starts in the future — treat as pending.
    return { ...base, status: 'PENDING', startAt: grant.startAt, expiresAt: grant.expiresAt, daysUntilExpiry };
  }

  // A claim newer than any revocation means the business has (re)paid and is
  // awaiting a grant — surface as PENDING so the owner sees progress.
  if (claim) {
    const revokedAfterClaim = grant?.revoked && grant.issuedAt >= claim.claimedAt;
    if (!revokedAfterClaim) return { ...base, status: 'PENDING' };
  }

  return { ...base, status: 'UNVERIFIED' };
}

/** Is this state one that grants the Verified badge + merchant discovery? */
export function isActivelyVerified(state: VerificationState): boolean {
  return state.status === 'VERIFIED';
}

// ── Owner expiry reminder ─────────────────────────────────────────

/**
 * The nearest reminder threshold that has been crossed but not yet acked,
 * or null. Purely presentational (dismissal is tracked in localStorage by
 * the caller). Only meaningful for a VERIFIED business.
 */
export function pendingExpiryNotice(state: VerificationState): number | null {
  if (state.status !== 'VERIFIED') return null;
  const d = state.daysUntilExpiry;
  for (const threshold of EXPIRY_NOTICE_DAYS) {
    if (d <= threshold) return threshold;
  }
  return null;
}

// ── Formatting ────────────────────────────────────────────────────

export function formatVerifyDate(unixSecs: number): string {
  if (!unixSecs) return '—';
  try {
    return new Date(unixSecs * 1000).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '—';
  }
}

/** All issuer pubkeys allowed to sign grants (re-exported for query authors). */
export const VERIFY_GRANT_ISSUERS = GRANT_ISSUERS;
